/**
 * Doserne login — password-only.
 *
 * OTP flow was removed because Messenger's in-app browser was sending codes
 * to a different session than the one requesting them, and some workers
 * were not receiving codes at all (spam/deliverability). Password login is
 * simpler, works everywhere, and admins can set a starter password when
 * inviting new hires. Users can update their password from Profile → Endre passord.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight, Loader2, Shield, KeyRound } from "lucide-react";
import { useDoserneAuth, TOKEN_KEY } from "./doserneAuth";
import InstallPrompt from "./InstallPrompt";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Logo() {
  return (
    <div className="inline-flex items-center gap-2">
      <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center shadow-sm">
        <Shield className="w-6 h-6 text-cyan-400" strokeWidth={2.5} />
      </div>
      <div className="flex flex-col leading-tight">
        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Doserne</span>
        <span className="text-lg font-black text-slate-900 -mt-0.5">Academy</span>
      </div>
    </div>
  );
}

export default function DoserneLogin() {
  const navigate = useNavigate();
  const { refresh } = useDoserneAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  const clearOtherSessions = () => {
    try {
      localStorage.removeItem("scaffoldx_token");
      localStorage.removeItem("scaffoldx_user");
      localStorage.removeItem("admin_view_as_role");
      localStorage.removeItem("impersonation_info");
    } catch (_e) { /* ignore */ }
  };

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/doserne/login`, {
        email: email.trim().toLowerCase(),
        password,
      });
      clearOtherSessions();
      localStorage.setItem(TOKEN_KEY, data.access_token);
      await refresh();
      toast.success(`Velkommen, ${data.user.first_name || data.user.email}!`);
      navigate("/doserne", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Feil e-post eller passord");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#fafaf7] flex flex-col">
      <header className="px-4 sm:px-6 py-5">
        <Logo />
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-6">
        <div className="w-full max-w-md">
          <div className="mb-6 text-center">
            <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight mb-2">
              Velkommen tilbake
            </h1>
            <p className="text-sm text-slate-500">
              Logg inn på Doserne Academy
            </p>
            <div className="mt-4 flex justify-center">
              <InstallPrompt />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 sm:p-8">
            <form onSubmit={submit} className="space-y-5" data-testid="doserne-login-form">
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">E-post</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="email" required autoFocus inputMode="email" autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg text-slate-900 placeholder:text-slate-400 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 focus:outline-none"
                    placeholder="deg@doserne.no"
                    data-testid="pwd-email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Passord</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="password" required autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg text-slate-900 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 focus:outline-none"
                    placeholder="••••••••"
                    data-testid="pwd-password"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={busy || !email.includes("@") || password.length < 6}
                className="w-full py-3 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 rounded-lg font-semibold text-sm shadow-sm flex items-center justify-center gap-2 transition"
                style={{ color: "#fff" }}
                data-testid="pwd-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Logg inn <ArrowRight className="w-4 h-4" /></>}
              </button>

              <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                Har du glemt passordet?{" "}
                <button type="button"
                        onClick={() => setShowForgot(true)}
                        className="text-cyan-600 hover:text-cyan-800 font-semibold underline"
                        data-testid="btn-forgot-open">
                  Nullstill via e-post
                </button>
                {" "}·{" "}
                <button type="button"
                        onClick={() => setShowOtp(true)}
                        className="text-cyan-600 hover:text-cyan-800 font-semibold underline"
                        data-testid="btn-otp-open">
                  Engangskode
                </button>
              </p>
            </form>
          </div>

          <p className="text-xs text-slate-400 text-center mt-6">
            Trenger du tilgang?{" "}
            <button type="button"
                    onClick={() => setShowRequest(true)}
                    className="text-cyan-600 hover:text-cyan-800 font-semibold underline"
                    data-testid="btn-request-access">
              Be om tilgang
            </button>
          </p>
        </div>
      </main>

      {showRequest && <AccessRequestModal onClose={() => setShowRequest(false)} />}
      {showForgot && <ForgotPasswordModal initialEmail={email} onClose={() => setShowForgot(false)} />}
      {showOtp && (
        <OtpLoginModal
          initialEmail={email}
          autoSend={email.includes("@")}
          onClose={() => setShowOtp(false)}
          onSuccess={async (data) => {
            clearOtherSessions();
            localStorage.setItem(TOKEN_KEY, data.access_token);
            await refresh();
            toast.success(`Velkommen, ${data.user.first_name || data.user.email}!`);
            navigate("/doserne", { replace: true });
          }}
        />
      )}

      <footer className="px-6 py-4 text-center text-xs text-slate-400 border-t border-slate-200">
        © {new Date().getFullYear()} Doserne Stillas AS — Internal team platform
      </footer>
    </div>
  );
}

function AccessRequestModal({ onClose }) {
  const [form, setForm] = React.useState({ email: "", full_name: "", phone: "", message: "" });
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [alreadyInvited, setAlreadyInvited] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.full_name.trim()) {
      toast.error("Fyll inn navn og e-post");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/doserne/request-access`, {
        email: form.email.trim().toLowerCase(),
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
      });
      setAlreadyInvited(!!data.already_invited);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke sende forespørselen");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="access-request-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black">Doserne Academy</div>
            <div className="text-lg font-black text-slate-900">Be om tilgang</div>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-900" data-testid="access-request-close">✕</button>
        </div>

        {done ? (
          <div className="p-6 text-center space-y-3">
            <div className="w-14 h-14 rounded-2xl bg-emerald-500/15 mx-auto flex items-center justify-center text-emerald-600 text-3xl">✓</div>
            {alreadyInvited ? (
              <>
                <div className="text-lg font-black text-slate-900">Kontoen din finnes allerede</div>
                <p className="text-sm text-slate-500">
                  Du har allerede tilgang. Skriv inn e-posten og passordet ditt over for å logge inn.
                </p>
              </>
            ) : (
              <>
                <div className="text-lg font-black text-slate-900">Forespørsel sendt</div>
                <p className="text-sm text-slate-500">
                  Administratoren vil gjennomgå forespørselen din snart. Du får en e-post med e-post + passord så snart tilgangen er godkjent.
                </p>
              </>
            )}
            <button onClick={onClose} className="mt-3 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-black uppercase tracking-widest" data-testid="access-request-done">
              Lukk
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-3">
            <p className="text-xs text-slate-500 leading-relaxed">
              Send oss en kort forespørsel og en administrator vil kontakte deg innen 24 timer.
            </p>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">Fullt navn *</label>
              <input type="text" required value={form.full_name}
                     onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                     className="w-full px-3 py-2 rounded-lg border-2 border-slate-200 text-slate-900 text-sm focus:outline-none focus:border-slate-900"
                     data-testid="req-fullname" placeholder="Fornavn Etternavn"/>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">E-post *</label>
              <input type="email" required value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })}
                     className="w-full px-3 py-2 rounded-lg border-2 border-slate-200 text-slate-900 text-sm focus:outline-none focus:border-slate-900"
                     data-testid="req-email" placeholder="deg@doserne.com"/>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">Telefon</label>
              <input type="tel" value={form.phone}
                     onChange={(e) => setForm({ ...form, phone: e.target.value })}
                     className="w-full px-3 py-2 rounded-lg border-2 border-slate-200 text-slate-900 text-sm focus:outline-none focus:border-slate-900"
                     data-testid="req-phone" placeholder="+47 ..."/>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">Melding</label>
              <textarea rows={3} value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border-2 border-slate-200 text-slate-900 text-sm focus:outline-none focus:border-slate-900 resize-none"
                        data-testid="req-message" placeholder="Hva ønsker du tilgang til?"/>
            </div>
            <button type="submit" disabled={busy}
                    className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-lg bg-slate-900 text-white text-sm font-black uppercase tracking-widest disabled:opacity-50"
                    data-testid="req-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : null}
              Send forespørsel
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// OTP FALLBACK — 6-digit e-mail code for accounts without a password.
// We deliberately do NOT show the magic link here; only the code, so
// the flow works from Messenger's in-app browser (which was breaking
// the magic-link handoff and forced us to remove OTP the first time).
// ---------------------------------------------------------------------
function OtpLoginModal({ initialEmail = "", autoSend = false, onClose, onSuccess }) {
  const [step, setStep] = React.useState("request"); // "request" | "verify"
  const [email, setEmail] = React.useState(initialEmail);
  const [code, setCode] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const autoFired = React.useRef(false);

  const requestCode = async (e) => {
    e?.preventDefault();
    if (!email.includes("@")) { toast.error("Skriv en gyldig e-post"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/doserne/auth/request-code`, {
        email: email.trim().toLowerCase(),
      });
      toast.success("Sjekk e-posten din for en 6-sifret kode");
      setStep("verify");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke sende kode");
    }
    setBusy(false);
  };

  // If the user already typed a valid e-mail on the login screen, send the
  // code immediately and jump straight to the "enter code" step — no need to
  // re-type the same e-mail (this was the confusing part).
  React.useEffect(() => {
    if (autoSend && !autoFired.current && (initialEmail || "").includes("@")) {
      autoFired.current = true;
      requestCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyCode = async (e) => {
    e?.preventDefault();
    if (code.trim().length !== 6) { toast.error("Koden må være 6 sifre"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/doserne/auth/verify-code`, {
        email: email.trim().toLowerCase(),
        code: code.trim(),
        remember_me: true,
      });
      await onSuccess?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ugyldig kode");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="otp-modal">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black">Login uten passord</div>
            <div className="text-lg font-black text-slate-900">
              {step === "request" ? "Få en engangskode" : "Skriv inn koden"}
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-900" data-testid="otp-close">✕</button>
        </div>

        {step === "request" ? (
          (autoSend && busy) ? (
            <div className="p-8 text-center space-y-3" data-testid="otp-autosending">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-600 mx-auto" />
              <p className="text-sm text-slate-600">Sender engangskode til<br /><span className="font-black text-slate-900">{email}</span></p>
            </div>
          ) : (
          <form onSubmit={requestCode} className="p-5 space-y-4">
            <p className="text-xs text-slate-500 leading-relaxed">
              Vi sender en 6-sifret kode til e-posten din. Ingen lenke — bare kopier
              tallene over hit. Fungerer selv fra Messenger og Facebook-appene.
            </p>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">E-post</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="email" required autoFocus inputMode="email" autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg text-slate-900 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 focus:outline-none"
                  placeholder="deg@doserne.no"
                  data-testid="otp-email"
                />
              </div>
            </div>
            <button type="submit" disabled={busy || !email.includes("@")}
                    className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-lg bg-slate-900 text-white text-sm font-black uppercase tracking-widest disabled:opacity-50"
                    data-testid="otp-send">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              Send kode
            </button>
          </form>
          )
        ) : (
          <form onSubmit={verifyCode} className="p-5 space-y-4">
            <p className="text-xs text-slate-500 leading-relaxed">
              Vi sendte en kode til <span className="font-black text-slate-900">{email}</span>.
              Koden er gyldig i 10 minutter.
            </p>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">6-sifret kode</label>
              <input
                type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} autoFocus autoComplete="one-time-code"
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="w-full px-4 py-3 text-center text-2xl font-black tracking-[0.5em] border border-slate-300 rounded-lg text-slate-900 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 focus:outline-none"
                placeholder="000000"
                data-testid="otp-code"
              />
            </div>
            <button type="submit" disabled={busy || code.length !== 6}
                    className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-lg bg-slate-900 text-white text-sm font-black uppercase tracking-widest disabled:opacity-50"
                    data-testid="otp-verify">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Logg inn
            </button>
            <button type="button" onClick={() => { setStep("request"); setCode(""); }}
                    className="w-full text-xs text-cyan-600 hover:text-cyan-800 underline"
                    data-testid="otp-resend">
              Send koden på nytt
            </button>
          </form>
        )}
      </div>
    </div>
  );
}



// -----------------------------------------------------------------------------
// FORGOT-PASSWORD MODAL — self-service reset link (30-min magic link via email)
// -----------------------------------------------------------------------------
function ForgotPasswordModal({ initialEmail = "", onClose }) {
  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!email.includes("@")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/doserne/auth/password-reset/request`, { email: email.trim().toLowerCase() });
      // Always show the generic success message regardless of whether
      // the email exists — matches the backend's no-enumeration policy.
      setSent(true);
    } catch (_e) {
      // Same generic message even on 500 — never leak whether email exists.
      setSent(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="forgot-modal">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-cyan-600" />
            <h2 className="text-sm font-black uppercase tracking-widest text-slate-900">Nullstill passord</h2>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-900" data-testid="forgot-close">✕</button>
        </div>

        {sent ? (
          <div className="p-6 text-center space-y-4" data-testid="forgot-sent">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <Mail className="w-7 h-7 text-emerald-600" />
            </div>
            <p className="text-sm text-slate-700">
              Hvis <span className="font-semibold">{email}</span> finnes hos oss, har vi sendt en lenke som er gyldig i 30 minutter.
            </p>
            <p className="text-xs text-slate-500">Sjekk innboksen og spam-mappen.</p>
            <button onClick={onClose}
                    className="mt-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-black uppercase tracking-widest"
                    style={{ color: "#fff" }}
                    data-testid="forgot-done">
              Lukk
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-4">
            <p className="text-xs text-slate-500 leading-relaxed">
              Skriv inn e-postadressen din, så sender vi en lenke der du kan velge et nytt passord.
            </p>
            <label className="block">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">E-post</div>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input type="email"
                       required
                       value={email}
                       onChange={(e) => setEmail(e.target.value)}
                       placeholder="deg@doserne.no"
                       autoFocus
                       className="w-full pl-8 pr-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                       data-testid="forgot-email" />
              </div>
            </label>
            <button type="submit"
                    disabled={busy || !email.includes("@")}
                    className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 rounded-lg font-semibold text-sm flex items-center justify-center gap-2"
                    style={{ color: "#fff" }}
                    data-testid="forgot-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send reset-lenke <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
